import './index.scss'
import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'

import * as axios from 'axios'
import * as Bowser from 'bowser'
import React from 'react'
import ReactDOM from 'react-dom'

import { amplitudeInit, brazeInit } from './lib/analytics/analyticsUtils'
import { getAppConfig } from './services/config'
import { getUser } from './lib/user'
import { initializeFirebase } from './lib/firebase'
import { preloadSvgCache } from './lib/svgCache'
import { setWindowConfig, addToConfig } from './lib/config'
import { startCacheMonitoring } from './lib/cacheMonitor'
import * as serviceWorker from './serviceWorker'
import App from './components/App'
import appModel from './models/app'
import arcConfig from '@arcgis/core/config'
import collectionsModel from './models/collections'
import dataManagerModel from './models/dataManager'
import dva from 'dva'
import ErrorScreen from './components/ErrorScreen'
import featuresModel from './models/features'
import historicalImageryModel from './models/historicalImagery'
import importSidebarModel from './models/importSidebar'
import layersModel from './models/layers'
import mapModel from './models/map'
import modalsModel from './models/modals'
import shareModel from './models/share'
import terrainModel from './models/terrain'
import toastModel from './models/toast'
import userModel from './models/user'
import userPreferencesModel from './models/userPreferences'

arcConfig.assetsPath = process.env.NODE_ENV == 'development' ? './assets' : process.env.PUBLIC_URL + '/assets'

let appConfig = {}
let uapiConfig = {}

const browserParser = Bowser.getParser(window.navigator.userAgent)
const isMobile = browserParser.getPlatformType(true) == 'mobile'
/* TODO: Check to see if condition below is needed */
// const isIE = browserParser.getBrowserName() == 'Internet Explorer';

if (isMobile) {
  window.location.replace('/download')
}

// Initialize app with async/await and separate try/catch blocks
(async () => {
  let appConfigRes = null
  let baseConfig = null
  let configObj = null
  let user = null

  // Load config.env.json
  try {
    baseConfig = await axios.get(process.env.PUBLIC_URL + '/config.env.json')
  } catch (error) {
    console.error('Failed to load config.env.json', error)
    ReactDOM.render(
      <ErrorScreen
        message='Failed to load application configuration. Please refresh the page or contact support if the problem persists.'
        title='Configuration Error'
        actions={[
          {
            label: 'Refresh Page',
            onAction: () => window.location.reload()
          },
          {
            label: 'Go to home page',
            onAction: () => window.location.replace('/')
          }
        ]}
      />,
      document.getElementById('root')
    )
    return
  }

  if (baseConfig) {
    appConfig = baseConfig.data
    configObj = baseConfig.data
    setWindowConfig({
      REACT_APP_AMPLITUDE_API_KEY: configObj.REACT_APP_AMPLITUDE_API_KEY,
      REACT_APP_BRAZE_SDK_KEY: configObj.REACT_APP_BRAZE_SDK_KEY,
      REACT_APP_GA_KEY: configObj.REACT_APP_GA_KEY,
      REACT_APP_GO2HUNT_SHOP_URL: configObj.REACT_APP_GO2HUNT_SHOP_URL,
      REACT_APP_USER_API_URL: configObj.REACT_APP_USER_API_URL,
      REACT_APP_USER_DATA_API_URL: configObj.REACT_APP_USER_DATA_API_URL
    })
  }

  // Get user (whoami call can work with cookies now that config is loaded)
  try {
    user = await Promise.resolve(getUser())
    if (!user) {
      const error = new Error('User not found')
      error.isUserError = true
      error.response = { status: 401 }
      throw error
    }
  } catch (error) {
    console.error('Failed to get user', error)
    const statusCode = error.response?.status || error.status
    const isUserError = error.isUserError || statusCode?.toString().startsWith('4')

    if (isUserError) {
      ReactDOM.render(
        <ErrorScreen
          message='You must be logged in to access Maps. Please log in and try again.'
          title='Authentication Required'
          timeout={5}
          timeoutMessage='Redirecting to login in {countdown}'
          actions={[
            {
              label: 'Go to Login',
              onAction: () => window.location.replace('/user/login?destination=/maps')
            }
          ]}
        />,
        document.getElementById('root')
      )
    } else {
      ReactDOM.render(
        <ErrorScreen
          message='Failed to load application configuration. Please refresh the page or contact support if the problem persists.'
          title='Configuration Error'
          actions={[
            {
              label: 'Refresh Page',
              onAction: () => window.location.reload()
            },
            {
              label: 'Go to home page',
              onAction: () => window.location.replace('/')
            }
          ]}
        />,
        document.getElementById('root')
      )
    }
    return
  }

  // Initialize Firebase
  try {
    initializeFirebase(configObj)
  } catch (error) {
    console.error('Failed to initialize Firebase', error)
    ReactDOM.render(
      <ErrorScreen
        message='Failed to initialize application services. Please refresh the page or contact support if the problem persists.'
        title='Initialization Error'
        actions={[
          {
            label: 'Refresh Page',
            onAction: () => window.location.reload()
          },
          {
            label: 'Go to home page',
            onAction: () => window.location.replace('/')
          }
        ]}
      />,
      document.getElementById('root')
    )
    return
  }

  // Get app config
  try {
    appConfigRes = await getAppConfig()
    if (!appConfigRes || !appConfigRes.success) {
      const error = new Error('Failed to fetch app config')
      error.isConfigError = true
      if (appConfigRes && appConfigRes.data && appConfigRes.data.response) {
        error.response = appConfigRes.data.response
      }
      throw error
    }
  } catch (error) {
    console.error('Failed to get app config', error)
    const statusCode = error.response?.status || error.status
    const isUserError = error.isUserError || statusCode?.toString().startsWith('4')

    if (isUserError) {
      ReactDOM.render(
        <ErrorScreen
          message="Please log in to access Maps. We'll redirect you to the login page in a moment."
          title='Login Required'
          timeout={5}
          timeoutMessage='Redirecting to login in {countdown}'
          actions={[
            {
              label: 'Go to Login',
              onAction: () => window.location.replace('/user/login?destination=/maps')
            }
          ]}
        />,
        document.getElementById('root')
      )
    } else {
      ReactDOM.render(
        <ErrorScreen
          message='Failed to load application configuration. Please refresh the page or contact support if the problem persists.'
          title='Configuration Error'
          actions={[
            {
              label: 'Refresh Page',
              onAction: () => window.location.reload()
            },
            {
              label: 'Go to home page',
              onAction: () => window.location.replace('/')
            }
          ]}
        />,
        document.getElementById('root')
      )
    }
    return
  }

  // Set axios defaults and configure app
  try {
    const config = appConfigRes.data
    uapiConfig = config
    addToConfig({
      BASE_SERVICES_URL: config.URL_BASE_SERVICES,
      BASEMAP_LAYERS: config.BASEMAP_LAYERS,
      BASEMAPS_SERVICE: config.URL_BASEMAP_SERVICES,
      FEEDBACK_URL: config.FEEDBACK_URL,
      GH_ASSETS_CLOUDFRONT_URL: config.GH_ASSETS_CLOUDFRONT_URL,
      LAYERS_MAJOR: config.LAYERS_MAJOR,
      LAYERS_MINOR: config.LAYERS_MINOR,
      MAPS_ENVIRONMENT: config.MAPS_ENVIRONMENT,
      MEMORY_MONITOR_ENABLED: config.MEMORY_MONITOR_ENABLED,
      REVERSE_GEOCODE_SERVICE: config.URL_AND_PATH_ARC_GIS_REVERSE_GEOCODE_SERVICE,
      SERVER_ARC_GIS: config.SERVER_ARC_GIS,
      SVG_CACHING_ENABLED: config.SVG_CACHING_ENABLED,
      WAYBACK_IMAGERY_CONFIG_URL: config.WAYBACK_IMAGERY_CONFIG_URL,
      WAYBACK_REQUEST_TOKEN: config.WAYBACK_REQUEST_TOKEN,
      WAYBACK_TILE_SERVICE_URL: config.WAYBACK_TILE_SERVICE_URL,
      WEB_LAYERS_MANIFEST_ENABLED: config.WEB_LAYERS_MANIFEST_ENABLED
    })

    if (process.env.REACT_APP_API_GATEWAY != null) {
      axios.defaults.baseURL = process.env.REACT_APP_API_GATEWAY
    } else {
      axios.defaults.baseURL = config.SERVER_GH_MAPS
    }
  } catch (error) {
    console.error('Failed to configure application', error)
    ReactDOM.render(
      <ErrorScreen
        message='Failed to configure application. Please refresh the page or contact support if the problem persists.'
        title='Configuration Error'
        actions={[
          {
            label: 'Refresh Page',
            onAction: () => window.location.reload()
          },
          {
            label: 'Go to home page',
            onAction: () => window.location.replace('/')
          }
        ]}
      />,
      document.getElementById('root')
    )
    return
  }

  // Initialize analytics and start app
  try {
    ga('create', window.__env.REACT_APP_GA_KEY, 'auto') // eslint-disable-line
    ga('send', 'pageview') // eslint-disable-line

    amplitudeInit(user)
    brazeInit(user)

    // SVG cache and monitoring only when appConfigRes has SVG_CACHING_ENABLED === true (module defaults to caching off)
    if (uapiConfig.SVG_CACHING_ENABLED === true) {
      preloadSvgCache(true)
      if (uapiConfig.MEMORY_MONITOR_ENABLED === true) {
        startCacheMonitoring()
      }
    }

    const app = dva()

    app.router(() => <App appConfig={{ ...appConfig, ...uapiConfig }} basemaps={uapiConfig.BASEMAPS.web} liveLayers={uapiConfig.LIVE_LAYERS} />)

    app.model(appModel)
    app.model(collectionsModel)
    app.model(dataManagerModel)
    app.model(featuresModel)
    app.model(historicalImageryModel)
    app.model(importSidebarModel)
    app.model(layersModel)
    app.model(mapModel)
    app.model(modalsModel)
    app.model(shareModel)
    app.model(terrainModel)
    app.model(toastModel)
    app.model(userModel)
    app.model(userPreferencesModel)

    app.start('#root')
  } catch (error) {
    console.error('Failed to start application', error)
    ReactDOM.render(
      <ErrorScreen
        message='Failed to start application. Please refresh the page or contact support if the problem persists.'
        title='Application Error'
        actions={[
          {
            label: 'Refresh Page',
            onAction: () => window.location.reload()
          },
          {
            label: 'Go to home page',
            onAction: () => window.location.replace('/')
          }
        ]}
      />,
      document.getElementById('root')
    )
  }
})()

// app._store.dispatch({type: 'app/gotoSidebar', payload: 'IMPORT'});

// ReactDOM.render(<App />, document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
